import { useEffect, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunCheck, ScenarioRunScenario } from '../../../api/api';
import { checkKind, checkWarns } from './runDetailModel';

const ICON: Record<string, IconProp> = {
    passed: 'circle-check',
    failed: 'circle-xmark',
    skipped: 'minus',
    warning: 'triangle-exclamation',
};

// The topics the runner files its checks under, in the order somebody reads a booking: is it there, are its dates
// right, did Exact spread it, does it add up, and how did the connector get there.
const TOPICS: { id: string; title: string; hint: string }[] = [
    { id: 'booking', title: 'Booked in Exact', hint: 'The sales entry the connector made' },
    { id: 'service-period', title: 'Service period', hint: 'From and To on the sales entry lines' },
    { id: 'spreading', title: 'Revenue spread', hint: 'How Exact spread the revenue over the months' },
    { id: 'amounts', title: 'Amounts', hint: 'Revenue, receivable and balance against the Chargebee invoice' },
    { id: 'connector', title: 'Connector', hint: 'How the connector processed the documents' },
];

// A run from before the runner filed checks under a topic groups them by oracle, under the same kind of title.
const ORACLES: { id: string; title: string; hint: string }[] = [
    { id: 'expectation', title: 'Booked in Exact', hint: 'What the suite expects the connector to book' },
    { id: 'accounting', title: 'Amounts and spreading', hint: 'Whether the booking adds up in Exact' },
    { id: 'pipeline', title: 'Connector', hint: 'How the connector processed the documents' },
];

interface Group {
    id: string;
    title: string;
    hint?: string;
    checks: ScenarioRunCheck[];
}

const groupsOf = (checks: ScenarioRunCheck[]): Group[] => {
    const byTopic = checks.every((check) => !!check.topic);
    const known = byTopic ? TOPICS : ORACLES;
    const keyOf = (check: ScenarioRunCheck) => (byTopic ? check.topic : check.oracle) ?? '';
    const ids = [
        ...known.map((group) => group.id),
        ...checks.map(keyOf).filter((id) => !known.some((group) => group.id === id)),
    ];

    return ids
        .map((id) => {
            const look = known.find((group) => group.id === id);

            return {
                id,
                title: look?.title ?? (id || 'Checks'),
                hint: look?.hint,
                checks: checks
                    .filter((check) => keyOf(check) === id)
                    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)),
            };
        })
        .filter((group) => group.checks.length > 0);
};

const CheckGroup = ({ group, scenario }: { group: Group; scenario: ScenarioRunScenario }) => {
    const { checks } = group;
    const failed = checks.filter((check) => checkKind(check.status) === 'failed').length;
    const skipped = checks.filter((check) => checkKind(check.status) === 'skipped').length;
    const warned = checks.filter((check) => checkWarns(scenario, check)).length;
    const [open, setOpen] = useState(failed > 0);

    useEffect(() => {
        if (failed > 0) setOpen(true);
    }, [failed]);

    const verdict = failed
        ? `${failed} of ${checks.length} failed`
        : skipped === checks.length
        ? `${checks.length} skipped`
        : `${checks.length - skipped} passed${skipped ? `, ${skipped} skipped` : ''}${
              warned ? ` · ${warned} with a note` : ''
          }`;

    return (
        <section className={`rd-checks__group${failed ? ' rd-checks__group--failed' : ''}`}>
            <button
                type="button"
                className="rd-checks__head"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                <FontAwesomeIcon icon={open ? 'angle-down' : 'angle-right'} className="rd-checks__chevron" />
                <span className="rd-checks__oracle">{group.title}</span>
                {group.hint && <span className="rd-checks__hint">{group.hint}</span>}
                <span className={`rd-checks__verdict${failed ? ' rd-checks__verdict--failed' : ''}`}>
                    {verdict}
                </span>
            </button>
            {open && (
                <table className="rd-checks__table">
                    <thead>
                        <tr>
                            <th aria-label="Outcome" />
                            <th>Check</th>
                            <th>Expected</th>
                            <th>Got</th>
                        </tr>
                    </thead>
                    <tbody>
                        {checks.map((check, index) => {
                            const kind = checkKind(check.status);
                            const look = kind === 'passed' && checkWarns(scenario, check) ? 'warning' : kind;

                            return (
                                <tr key={index} className={`rd-checks__row rd-checks__row--${look}`}>
                                    <td className="rd-checks__icon">
                                        <FontAwesomeIcon icon={ICON[look]} />
                                    </td>
                                    <td className="rd-checks__name">
                                        {check.name}
                                        {check.detail && <div className="rd-checks__detail">{check.detail}</div>}
                                    </td>
                                    <td className="rd-checks__value">{check.expected}</td>
                                    <td className="rd-checks__value rd-checks__value--actual">{check.actual}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </section>
    );
};

/** Every check of a scenario, grouped by what it is about and in the order somebody would ask the questions. */
export const CheckTable = ({ scenario }: { scenario: ScenarioRunScenario }) => (
    <div className="rd-checks">
        {groupsOf(scenario.checks ?? []).map((group) => (
            <CheckGroup key={group.id} group={group} scenario={scenario} />
        ))}
    </div>
);
